// Root component.
// - Path-based auth routes: `/reset-password` and `/auth/confirm` both
//   land here from email callbacks, otherwise we render the main shell.
// - Auth gate: signed-in users always see the app; guests see the app too
//   (with local data) but get a "Sign in" path from Profile. When the
//   caller asks to sign in, we render AuthScreen on top of the shell.
// - Realtime: subscribes to Postgres CDC for the current user's tables
//   and invalidates the matching TanStack Query caches so multi-device
//   use stays in sync.
//
// Shell selection (desktop vs phone):
//   • Capacitor (native iOS WebView)   → PhoneFrame, untouched
//   • Browser viewport < `DESKTOP_MIN` → PhoneFrame (the centered
//       402×874 "iPhone in a dark canvas" preview used for marketing
//       screenshots and narrow PWAs)
//   • Browser viewport ≥ `DESKTOP_MIN` → DesktopShell (a window-shaped
//       layout with a left sidebar, edge safe-area padding, and a
//       drag region for the macOS hidden-inset titlebar / traffic
//       lights). The same scene components render in either shell —
//       only the chrome around them changes.

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Icon } from './components/Icon.jsx';
import { NowPlayingBar } from './components/NowPlayingBar.jsx';
import { AIConsentModal } from './components/AIConsentModal.jsx';
import { PaywallSheet } from './components/PaywallSheet.jsx';
import { Dashboard } from './scenes/Dashboard.jsx';
import { DesktopHome } from './scenes/DesktopHome.jsx';
import { Analytics } from './scenes/Analytics.jsx';
import { Briefing } from './scenes/Briefing.jsx';
import { Intelligence } from './scenes/Intelligence.jsx';
import { Profile } from './scenes/Profile.jsx';
import { AddHabitSheet } from './scenes/AddHabitSheet.jsx';
import { SaveProgressSheet } from './scenes/SaveProgressSheet.jsx';
import { AuthScreen } from './auth/AuthScreen.jsx';
import { ResetPassword } from './auth/ResetPassword.jsx';
import { ConfirmEmail } from './auth/ConfirmEmail.jsx';
import { useAuth } from './auth/useAuth.js';
import { supabase } from './lib/supabase.js';
import { getToday } from './lib/constants.js';
import { sfx } from './lib/sfx.js';
import { todayIsoKey } from './lib/dateKeys.js';
import { softPromptShown, markSoftPromptShown } from './lib/onboardingFlags.js';

// ── Realtime subscriptions ─────────────────────────────────────────
// Fires once per auth state. Subscribes to all user-scoped tables and
// invalidates the corresponding query cache on change. The RLS policy on
// the server still filters these to the current user, but we also scope
// the channel filter to be explicit and keep bandwidth down.
function useRealtime(userId) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!userId) return;
    const today = todayIsoKey();
    const ch = supabase
      .channel(`rt:${userId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'habits',          filter: `user_id=eq.${userId}` }, () => qc.invalidateQueries({ queryKey: ['habits']   }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'checkins',        filter: `user_id=eq.${userId}` }, () => qc.invalidateQueries({ queryKey: ['checkins'] }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'goals',           filter: `user_id=eq.${userId}` }, () => qc.invalidateQueries({ queryKey: ['goals']    }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'briefing_tasks',  filter: `user_id=eq.${userId}` }, () => qc.invalidateQueries({ queryKey: ['briefing', today] }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'briefing_meta',   filter: `user_id=eq.${userId}` }, () => qc.invalidateQueries({ queryKey: ['briefing', today] }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles',        filter: `id=eq.${userId}`      }, () => qc.invalidateQueries({ queryKey: ['profile']  }))
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [userId, qc]);
}

// ── Viewport ────────────────────────────────────────────────────────
// Breakpoint at which we leave the phone-shaped preview and become a
// real desktop window. 900px is large enough that the sidebar + main
// pane don't feel cramped, and small enough that landscape iPads and
// half-screen browser windows pick up the desktop UI rather than a
// black bar around a 402px iPhone.
const DESKTOP_MIN = 900;

// Subscribes to viewport width changes via `matchMedia` so we only
// re-render the entire shell when the user actually crosses the
// breakpoint — not on every `resize` tick.
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    // The desktop shell is web-only — Capacitor is always the phone
    // shell regardless of viewport, because the native iOS shell hosts
    // it inside a real device frame.
    if (document.body.classList.contains('capacitor')) return false;
    return window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (document.body.classList.contains('capacitor')) return;
    const mq = window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`);
    const on = (e) => setIsDesktop(e.matches);
    // Safari < 14 fires `addListener` instead of `addEventListener`.
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on);
    };
  }, []);
  return isDesktop;
}

// ── Particles / phone frame (decorative) ────────────────────────────
function PhoneFrame({ children }) {
  return (
    <div className="stage">
      <div className="phone">
        <div className="lamp">
          <div className="blob b1"/>
          <div className="blob b2"/>
          <div className="blob b3"/>
        </div>
        <div className="particles">
          {Array.from({ length: 26 }).map((_, i) => (
            <div key={i} className="particle" style={{
              left: ((i * 37) % 100) + '%',
              bottom: (-40 - ((i * 53) % 280)) + 'px',
              opacity: 0.3 + ((i * 0.17) % 0.55),
              animation: `rise ${24 + ((i * 1.3) % 18)}s linear infinite`,
              animationDelay: `-${(i * 1.7) % 24}s`,
              width: (i % 5 === 0) ? '3px' : '2px',
              height: (i % 5 === 0) ? '3px' : '2px',
            }}/>
          ))}
        </div>
        <div className="dynamic-island"/>
        <div className="screen">
          {children}
        </div>
        <div className="home-indicator"/>
      </div>
    </div>
  );
}

export function App() {
  // Very lightweight client-side routing — we have exactly one extra route.
  const [path, setPath] = useState(typeof location !== 'undefined' ? location.pathname : '/');
  useEffect(() => {
    const onPop = () => setPath(location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  if (path === '/reset-password') {
    return <ResetPassword/>;
  }
  if (path === '/auth/confirm') {
    return <ConfirmEmail/>;
  }

  return <AppShell/>;
}

// ── Desktop shell ──────────────────────────────────────────────────
// Window-shaped layout that replaces PhoneFrame on wide viewports.
//
// Edge "safety zone" / buffer:
//   • The outer container reserves padding via `env(safe-area-inset-*)`
//     for environments that expose them (notched browser windows, the
//     Electron window with Window Controls Overlay enabled).
//   • A 36px-tall drag region across the very top stays clear of the
//     macOS hidden-inset traffic lights so the content underneath
//     never collides with them, and gives the user an empty strip to
//     grab when moving the window (`-webkit-app-region: drag` is set
//     in CSS via the `.desk-drag` class).
//
// Navigation:
//   • Sidebar replaces the bottom tab bar. The five buttons map 1:1 to
//     the same `tab` state used by PhoneFrame, so scenes don't care
//     which shell hosts them.
//   • Brand mark in the sidebar uses the same C-with-check glyph as the
//     marketing site for visual continuity between landing and product.
function DesktopShell({ tab, setTab, onOpenAuth, status, children }) {
  // Landing site we navigate back to from the Home/Download pills. Kept as a
  // single constant so a domain swap (consistency.proxera.io once the CNAME
  // lands) is a one-line change here, not a hunt across the file.
  const LANDING = 'https://consistency.proxera.io';

  // Moonshot-style nav: deepen the glass + shadow once the user has
  // scrolled the inner content area past ~100 px. We listen on the
  // .desk-main scroll container (which is the only thing that actually
  // scrolls in the desktop shell — the outer body uses CSS grid and
  // does not scroll) and toggle a class on the .site-nav-wrap. The
  // CSS in index.css picks up `.site-nav-wrap.scrolled .site-nav` and
  // intensifies the background tint + drop shadow.
  useEffect(() => {
    const scroller = document.querySelector('.desk-main');
    const wrap = document.querySelector('.site-nav-wrap');
    if (!scroller || !wrap) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        wrap.classList.toggle('scrolled', scroller.scrollTop > 100);
        ticking = false;
      });
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="desk-shell">
      {/* Top window drag region — sits above everything so it can grab
          mouse-down even when the user clicks near content edges. The
          left ~80px is left transparent so the macOS traffic lights show
          through; clicks there are absorbed by the OS, not by us. */}
      <div className="desk-drag" aria-hidden="true" />

      {/*
        Site nav — pixel-for-pixel the same pill-glass header that the
        marketing site uses, so a visitor on the Home tab who clicks
        through to "Web app" sees an identical bar of pills here and can
        keep navigating (back to Home, on to Download). "Web app" is the
        active pill since we're rendering it. CSS for this lives in
        index.css under `.site-nav-*` and intentionally mirrors the
        landing's `.nav` rules so the two surfaces stay in lockstep.
      */}
      <div className="site-nav-wrap">
        <nav className="site-nav">
          <a className="site-nav-brand" href={LANDING}>
            <span className="site-nav-glyph">
              {/* Brand mark: blue C-with-check PNG that lives in /public.
                  The inline outline SVG previously used here read as a
                  different mark than the iOS app icon and the landing
                  page header, so the three surfaces now share a single
                  asset (logo.png) for visual consistency. */}
              <img src="/logo.png" alt="" aria-hidden="true"/>
            </span>
            <span className="site-nav-name">Consist<em>e</em>ncy</span>
          </a>
          <div className="site-nav-tabs">
            <a className="site-nav-tab" href={LANDING}>Home</a>
            <a className="site-nav-tab active" aria-current="page" href="#">Web app</a>
            <a className="site-nav-tab" href={`${LANDING}#download`}>Download</a>
          </div>
          <div className="site-nav-cta">
            {status === 'guest' && (
              <button className="site-nav-btn site-nav-btn-ghost" onClick={onOpenAuth}>Sign in</button>
            )}
            <a className="site-nav-btn site-nav-btn-primary" href={`${LANDING}#download`}>Download ↗</a>
          </div>
        </nav>
      </div>

      <aside className="desk-side">
        <div className="desk-brand">
          <span className="desk-brand-glyph">
            <img src="/logo.png" alt="" aria-hidden="true"/>
          </span>
          <span className="desk-brand-name">Consist<em>e</em>ncy</span>
        </div>

        <div className="desk-nav-group">Workspace</div>
        <button className={'desk-nav' + (tab === 'home' ? ' on' : '')} onClick={() => { if (tab !== 'home') sfx.nav && sfx.nav(); setTab('home'); }}>
          <Icon name="grid" size={18}/><span>Dashboard</span>
        </button>
        <button className={'desk-nav' + (tab === 'analytics' ? ' on' : '')} onClick={() => { if (tab !== 'analytics') sfx.nav && sfx.nav(); setTab('analytics'); }}>
          <Icon name="chart" size={18}/><span>Analytics</span>
        </button>
        <button className={'desk-nav' + (tab === 'intelligence' ? ' on' : '')} onClick={() => { if (tab !== 'intelligence') sfx.nav && sfx.nav(); setTab('intelligence'); }}>
          <Icon name="brain" size={18}/><span>Intel Brief</span>
        </button>
        <button className={'desk-nav' + (tab === 'briefing' ? ' on' : '')} onClick={() => { if (tab !== 'briefing') sfx.nav && sfx.nav(); setTab('briefing'); }}>
          <Icon name="clipboard" size={18}/><span>Agenda</span>
        </button>

        <div className="desk-nav-group">Account</div>
        <button className={'desk-nav' + (tab === 'profile' ? ' on' : '')} onClick={() => { if (tab !== 'profile') sfx.nav && sfx.nav(); setTab('profile'); }}>
          <Icon name="user" size={18}/><span>Profile</span>
        </button>
      </aside>

      <main className="desk-main">
        {/* Inner scroll container — the existing scenes already render
            their own header/title, so we just give them width and
            edge padding. */}
        <div className="desk-content">
          {children}
        </div>
        <footer className="desk-footer">
          <span className="desk-footer-l">
            <span className="desk-brand-glyph desk-brand-glyph--footer">
              <img src="/logo.png" alt="" aria-hidden="true"/>
            </span>
            © 2026 Proxera · Consistency
          </span>
          <span className="desk-footer-r">
            <a href="https://consistency.proxera.io" target="_blank" rel="noopener noreferrer">Home</a>
            <a href="https://consistency.proxera.io#download" target="_blank" rel="noopener noreferrer">Desktop app</a>
            <a href="mailto:noah@proxera.io">Contact</a>
          </span>
        </footer>
      </main>
    </div>
  );
}

function AppShell() {
  const { status, user } = useAuth();
  const isDesktop = useIsDesktop();
  const today = getToday();
  const [year, setYear] = useState(today.year);
  const [month, setMonth] = useState(today.month);
  // Always boot on the Dashboard. We used to rehydrate the last tab from
  // localStorage so a reload preserved where the user was, but product
  // decision: the Dashboard is the "home base" of the app and every cold
  // launch should land there. The in-session tab state still works normally
  // — it just doesn't persist across app restarts.
  const [tab, setTab] = useState('home');
  const [addOpen, setAddOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  // Soft signup prompt state. Variant controls the copy (habit vs briefing).
  const [softPrompt, setSoftPrompt] = useState({ open: false, variant: 'habit' });

  // When the now-playing mini bar is tapped on another tab, we flip to the
  // Intelligence tab and hand it the brief id to open. Cleared once the
  // tab has consumed it so subsequent taps work.
  const [requestedBriefId, setRequestedBriefId] = useState(null);

  // (Removed the localStorage persistence for `tab` — the boot rule above
  // explicitly lands every cold launch on the Dashboard, so persisting the
  // last tab would immediately contradict the rehydration and waste a
  // write on every tap.)

  // Wire realtime once we know the user id.
  useRealtime(user?.id || null);

  // Re-sync ALL notification schedules on every cold boot. iOS sometimes
  // drops scheduled notifications across new builds; calling bootSync on
  // both modules keeps the OS-side schedule in lockstep with prefs.
  useEffect(() => {
    import('./lib/eveningRecap.js').then(m => m.bootSync()).catch(() => {});
    import('./lib/notifications.js').then(m => m.bootSync()).catch(() => {});
  }, []);

  // If auth flips to signed-in while the AuthScreen is open, dismiss it.
  useEffect(() => {
    if (status === 'signed-in') {
      setAuthOpen(false);
      setSoftPrompt(s => ({ ...s, open: false }));
    }
  }, [status]);

  // Soft-prompt triggers — fired by scenes at "value moments" for guests.
  // Each call is a no-op if the prompt has been shown before on this device,
  // if the user is already signed in, or if the auth sheet is already open.
  const maybeFireSoftPrompt = (variant) => {
    if (status !== 'guest') return;
    if (softPromptShown()) return;
    if (authOpen) return;
    setSoftPrompt({ open: true, variant });
  };
  const handleSoftSignUp = () => {
    markSoftPromptShown();
    setSoftPrompt(s => ({ ...s, open: false }));
    setAuthOpen(true);
  };
  const handleSoftDismiss = () => {
    markSoftPromptShown();
    setSoftPrompt(s => ({ ...s, open: false }));
  };
  const openAuth = () => { sfx.nav && sfx.nav(); setAuthOpen(true); };

  // The actual scene content is identical in both shells — only the chrome
  // (phone bezel vs window with sidebar) differs. We render it once and
  // hand it to whichever shell is active. The Now-playing mini bar is
  // included for the phone shell where it sits above the tab bar; on
  // desktop we suppress it because the global sidebar already exposes the
  // Intel Brief tab as a first-class destination.
  const scenes = (
    <>
      {tab === 'home' && (
        isDesktop ? (
          <DesktopHome
            onOpenAdd={() => { sfx.sheetOpen && sfx.sheetOpen(); setAddOpen(true); }}
            onSwitchTab={(t, briefId) => {
              if (briefId) setRequestedBriefId(briefId);
              setTab(t);
            }}
          />
        ) : (
          <Dashboard
            month={month}
            year={year}
            isGuest={status === 'guest'}
            onSetMonth={(y, m) => { sfx.month && sfx.month(); setYear(y); setMonth(m); }}
            onOpenAdd={() => { sfx.sheetOpen && sfx.sheetOpen(); setAddOpen(true); }}
            onFirstHabitCreated={() => maybeFireSoftPrompt('habit')}
            onRequestSignIn={openAuth}
          />
        )
      )}
      {tab === 'analytics' && (
        <Analytics
          month={month} year={year}
          onOpenSettings={() => setTab('profile')}
        />
      )}
      {tab === 'briefing' && (
        <Briefing onFirstBriefingGenerated={() => maybeFireSoftPrompt('briefing')}/>
      )}
      {tab === 'intelligence' && (
        <Intelligence
          isGuest={status === 'guest'}
          onRequestSignIn={openAuth}
          openBriefId={requestedBriefId}
          onBriefOpened={() => setRequestedBriefId(null)}
        />
      )}
      {tab === 'profile' && (
        <Profile onRequestSignIn={() => setAuthOpen(true)}/>
      )}
    </>
  );

  // Modal overlays sit at the top of the React tree regardless of shell so
  // they always render above the active scene without z-index gymnastics.
  const overlays = (
    <>
      <AddHabitSheet
        open={addOpen}
        onClose={() => { sfx.sheetClose && sfx.sheetClose(); setAddOpen(false); }}
      />
      <SaveProgressSheet
        open={softPrompt.open}
        variant={softPrompt.variant}
        onSignUp={handleSoftSignUp}
        onDismiss={handleSoftDismiss}
      />
      <AIConsentModal/>
      <PaywallSheet/>
      {authOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(20px)',
            overflowY: 'auto',
          }}
        >
          <div style={{ position: 'absolute', top: 10, right: 14, zIndex: 1 }}>
            <button
              onClick={() => setAuthOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid var(--stroke-1)',
                color: 'var(--fg-3)', borderRadius: 999,
                padding: '6px 12px', fontFamily: 'var(--font-display)',
                fontWeight: 600, fontSize: 12, cursor: 'pointer',
              }}
            >Close</button>
          </div>
          <AuthScreen
            onGuest={() => setAuthOpen(false)}
          />
        </div>
      )}
    </>
  );

  if (isDesktop) {
    return (
      <>
        <DesktopShell tab={tab} setTab={setTab} onOpenAuth={openAuth} status={status}>
          {scenes}
        </DesktopShell>
        {overlays}
      </>
    );
  }

  return (
    <PhoneFrame>
      <div className="scroll">
        {scenes}
      </div>

      <NowPlayingBar
        hidden={tab === 'intelligence'}
        onJumpToBrief={(id) => {
          setRequestedBriefId(id);
          setTab('intelligence');
        }}
      />

      <div className="tab-bar">
        <button className={'tab-item' + (tab === 'home' ? ' on' : '')}
          onClick={() => { if (tab !== 'home') sfx.nav && sfx.nav(); setTab('home'); }}>
          <Icon name="grid" size={19}/>
          <span>Dashboard</span>
        </button>
        <button className={'tab-item' + (tab === 'analytics' ? ' on' : '')}
          onClick={() => { if (tab !== 'analytics') sfx.nav && sfx.nav(); setTab('analytics'); }}>
          <Icon name="chart" size={19}/>
          <span>Analytics</span>
        </button>
        <button className={'tab-item' + (tab === 'intelligence' ? ' on' : '')}
          onClick={() => { if (tab !== 'intelligence') sfx.nav && sfx.nav(); setTab('intelligence'); }}>
          <Icon name="brain" size={19}/>
          <span>Intel Brief</span>
        </button>
        <button className={'tab-item' + (tab === 'briefing' ? ' on' : '')}
          onClick={() => { if (tab !== 'briefing') sfx.nav && sfx.nav(); setTab('briefing'); }}>
          <Icon name="clipboard" size={19}/>
          <span>Agenda</span>
        </button>
        <button className={'tab-item' + (tab === 'profile' ? ' on' : '')}
          onClick={() => { if (tab !== 'profile') sfx.nav && sfx.nav(); setTab('profile'); }}>
          <Icon name="user" size={19}/>
          <span>Profile</span>
        </button>
      </div>

      {overlays}
    </PhoneFrame>
  );
}
